/**
 * Mad Dad Labz Specific Easter Egg Implementations
 * Keyboard accessible, visual-only effects, no checkout interference
 */

class MadDadSpecificEggs {
  constructor() {
    this.konamiSequence = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];
    this.konamiProgress = [];
    this.scrollPattern = [];
    this.longPressTimer = null;
    this.isLongPressing = false;
    
    this.init();
  }

  init() {
    // Wait for main Easter egg system to be ready
    if (window.madDadEggs && !window.madDadEggs.isOptedOut) {
      this.bindSpecificEggs();
    } else {
      // Retry after a short delay
      setTimeout(() => this.init(), 100);
    }
  }

  revealSecretContent() {
    const secretEl = document.querySelector('[data-mad-dad-secret]');
    if (!secretEl) {
      return;
    }

    secretEl.removeAttribute('hidden');
    secretEl.setAttribute('aria-hidden', 'false');
    secretEl.classList.add('mad-dad-secret--revealed');

    if (!secretEl.style.opacity) {
      secretEl.style.opacity = '1';
    }

    setTimeout(() => {
      secretEl.classList.remove('mad-dad-secret--revealed');
    }, 8000);
  }

  bindSpecificEggs() {
    this.setupKonamiCode();
    this.setupLogoLongPress();
    this.setupScrollPattern();
    this.setupTimeBasedEgg();
    this.setupInteractionCombo();
  }

  /**
   * Easter Egg 1: Konami Code
   * Sequence: ↑↑↓↓←→←→BA
   */
  setupKonamiCode() {
    document.addEventListener('mad-dad-egg-keydown', (event) => {
      const { code } = event.detail;
      
      // Check if this key matches the next expected key in sequence
      const expectedKey = this.konamiSequence[this.konamiProgress.length];
      
      if (code === expectedKey) {
        this.konamiProgress.push(code);
        
        // Complete sequence
        if (this.konamiProgress.length === this.konamiSequence.length) {
          this.triggerKonamiReward();
          this.konamiProgress = []; // Reset
        }
      } else {
        // Wrong key, reset sequence
        this.konamiProgress = [];
        // Check if this key could start the sequence
        if (code === this.konamiSequence[0]) {
          this.konamiProgress.push(code);
        }
      }
    });
  }

  triggerKonamiReward() {
    if (!window.madDadEggs) {
      return;
    }

    const alreadyDiscovered = typeof window.madDadEggs.isEggDiscovered === 'function'
      ? window.madDadEggs.isEggDiscovered('konami-code')
      : false;

    const discoveredNow = window.madDadEggs.discoverEgg('konami-code', '🎮 Laboratory access granted!');

    if (!discoveredNow && alreadyDiscovered && typeof window.madDadEggs.showEggReward === 'function') {
      window.madDadEggs.showEggReward('konami-code', '🎮 Laboratory access granted!');
    }

    if (discoveredNow || alreadyDiscovered) {
      // Create special visual effect
      this.createColorPaletteExplosion();

      // Temporarily reveal hidden product variant or special message
      this.revealSecretContent();
    }
  }

  /**
   * Easter Egg 2: Logo Long Press (3 seconds)
   */
  setupLogoLongPress() {
    const logos = document.querySelectorAll('[class*="logo"], .header__heading, .site-header__logo');
    
    logos.forEach(logo => {
      // Mouse events
      logo.addEventListener('mousedown', (e) => this.startLongPress(e, 'logo-long-press'));
      logo.addEventListener('mouseup', () => this.endLongPress());
      logo.addEventListener('mouseleave', () => this.endLongPress());
      
      // Touch events for mobile
      logo.addEventListener('touchstart', (e) => this.startLongPress(e, 'logo-long-press'));
      logo.addEventListener('touchend', () => this.endLongPress());
      logo.addEventListener('touchcancel', () => this.endLongPress());
      
      // Keyboard events for accessibility
      logo.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          this.startLongPress(e, 'logo-long-press');
        }
      });
      logo.addEventListener('keyup', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          this.endLongPress();
        }
      });
    });
  }

  startLongPress(event, eggId) {
    if (this.isLongPressing) return;
    
    this.isLongPressing = true;
    
    // Shorter duration on mobile for better UX
    const isMobile = 'ontouchstart' in window;
    const duration = isMobile ? 2000 : 3000;
    
    this.longPressTimer = setTimeout(() => {
      this.triggerLongPressReward(eggId);
    }, duration);
    
    // Enhanced visual feedback for touch devices
    const target = event.target;
    target.style.transform = 'scale(0.95)';
    target.style.transition = 'transform 0.1s ease';
    
    // Add progress indicator for mobile
    if (isMobile) {
      this.showLongPressProgress(target, duration);
    }
  }

  showLongPressProgress(target, duration) {
    const progressRing = document.createElement('div');
    progressRing.className = 'long-press-progress';
    progressRing.style.cssText = `
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      border: 2px solid transparent;
      border-top-color: #52c301;
      border-radius: 50%;
      animation: longPressProgress ${duration}ms linear;
      pointer-events: none;
      z-index: 1000;
    `;
    
    // Add animation if not present
    if (!document.querySelector('#long-press-styles')) {
      const style = document.createElement('style');
      style.id = 'long-press-styles';
      style.textContent = `
        @keyframes longPressProgress {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
    
    target.style.position = 'relative';
    target.appendChild(progressRing);
    
    // Remove progress ring when long press ends
    setTimeout(() => {
      if (progressRing.parentNode) {
        progressRing.remove();
      }
    }, duration);
  }

  endLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    this.isLongPressing = false;
    
    // Reset visual feedback
    const logos = document.querySelectorAll('[class*="logo"], .header__heading, .site-header__logo');
    logos.forEach(logo => {
      logo.style.transform = '';
      logo.style.transition = '';
      
      // Remove progress ring if present
      const progressRing = logo.querySelector('.long-press-progress');
      if (progressRing) {
        progressRing.remove();
      }
    });
  }

  triggerLongPressReward(eggId) {
    if (window.madDadEggs.discoverEgg(eggId, '🎨 Color palette unlocked!')) {
      this.createColorPaletteAnimation();
    }
    this.endLongPress();
  }

  /**
   * Easter Egg 3: Scroll Pattern Recognition
   * Pattern: Scroll down, up, down, up, down (within 5 seconds)
   */
  setupScrollPattern() {
    let lastScrollY = window.scrollY;
    let patternStartTime = null;
    const requiredPattern = ['down', 'up', 'down', 'up', 'down'];
    
    document.addEventListener('mad-dad-egg-scroll', (event) => {
      const { scrollY } = event.detail;
      const now = Date.now();

      // Reset pattern if too much time has passed
      if (patternStartTime && now - patternStartTime > 5000) {
        this.scrollPattern = [];
        patternStartTime = null;
      }

      const previousScrollY = lastScrollY;
      const distance = Math.abs(scrollY - previousScrollY);

      // Only track significant scroll movements (>50px)
      if (distance < 50) {
        return;
      }

      // Determine scroll direction
      const direction = scrollY > previousScrollY ? 'down' : 'up';
      lastScrollY = scrollY;

      // Start tracking pattern
      if (this.scrollPattern.length === 0) {
        patternStartTime = now;
      }

      // Add to pattern if it matches expected sequence
      const expectedDirection = requiredPattern[this.scrollPattern.length];
      if (direction === expectedDirection) {
        this.scrollPattern.push(direction);
        
        // Check if pattern is complete
        if (this.scrollPattern.length === requiredPattern.length) {
          this.triggerScrollPatternReward();
          this.scrollPattern = [];
          patternStartTime = null;
        }
      } else {
        // Reset pattern on wrong direction
        this.scrollPattern = [];
        patternStartTime = null;
      }
    });
  }

  triggerScrollPatternReward() {
    if (window.madDadEggs.discoverEgg('scroll-pattern', '📜 Scroll master detected!')) {
      this.createScrollWaveEffect();
    }
  }

  /**
   * Easter Egg 4: Time-Based Discovery
   * Different experiences based on visit time
   */
  setupTimeBasedEgg() {
    const hour = new Date().getHours();
    let eggId = null;
    let message = null;
    
    // Laboratory hours: 9 AM - 5 PM
    if (hour >= 9 && hour <= 17) {
      eggId = 'lab-hours';
      message = '🧪 Laboratory is open!';
    }
    // After hours: 6 PM - 8 AM
    else {
      eggId = 'after-hours';
      message = '🌙 Night shift experiments active!';
    }
    
    // Trigger after 30 seconds on page
    setTimeout(() => {
      if (window.madDadEggs && !window.madDadEggs.isEggDiscovered(eggId)) {
        window.madDadEggs.discoverEgg(eggId, message);
      }
    }, 30000);
  }

  /**
   * Easter Egg 5: Interaction Combo
   * Specific sequence: Click logo, scroll to footer, click footer logo (if exists)
   */
  setupInteractionCombo() {
    let comboState = 'waiting-for-logo';
    let comboTimer = null;
    
    document.addEventListener('mad-dad-egg-click', (event) => {
      const { target } = event.detail;
      const footerEl = document.querySelector('footer, .footer');

      // Reset combo after 10 seconds of inactivity
      if (comboTimer) {
        clearTimeout(comboTimer);
      }
      comboTimer = setTimeout(() => {
        comboState = 'waiting-for-logo';
      }, 10000);
      
      switch (comboState) {
        case 'waiting-for-logo':
          if (this.isLogo(target)) {
            comboState = 'waiting-for-footer-scroll';
          }
          break;
          
        case 'waiting-for-footer-scroll':
          // Check if user scrolled to footer area
          if (footerEl) {
            const footerRect = footerEl.getBoundingClientRect();
            if (footerRect.top < window.innerHeight) {
              comboState = 'waiting-for-footer-click';
            }
          }
          break;
          
        case 'waiting-for-footer-click':
          if (footerEl && footerEl.contains(target)) {
            this.triggerComboReward();
            comboState = 'waiting-for-logo'; // Reset
          }
          break;
      }
    });
  }

  triggerComboReward() {
    if (window.madDadEggs.discoverEgg('interaction-combo', '🎯 Perfect sequence!')) {
      this.createComboEffect();
    }
  }

  /**
   * Visual Effects for Easter Eggs
   */
  createColorPaletteExplosion() {
    const colors = ['#051c36', '#01dbf3', '#52c301', '#FFFDF6', '#d11882', '#fc760c'];
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;
    
    colors.forEach((color, index) => {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 20px;
        height: 20px;
        background: ${color};
        border-radius: 50%;
        top: 50%;
        left: 50%;
        animation: explode-${index} 2s ease-out forwards;
      `;
      container.appendChild(particle);
    });
    
    // Add explosion animation
    const style = document.createElement('style');
    style.textContent = colors.map((_, index) => `
      @keyframes explode-${index} {
        0% { transform: translate(-50%, -50%) scale(0); opacity: 1; }
        100% { 
          transform: translate(${(index - 2.5) * 200}px, ${Math.sin(index) * 200}px) scale(1); 
          opacity: 0; 
        }
      }
    `).join('');
    document.head.appendChild(style);
    
    document.body.appendChild(container);
    
    setTimeout(() => {
      container.remove();
      style.remove();
    }, 2000);
  }

  createColorPaletteAnimation() {
    const logo = document.querySelector('[class*="logo"], .header__heading');
    if (!logo) return;
    
    const colors = ['#051c36', '#01dbf3', '#52c301', '#d11882', '#fc760c'];
    let colorIndex = 0;
    
    const interval = setInterval(() => {
      logo.style.color = colors[colorIndex];
      colorIndex = (colorIndex + 1) % colors.length;
    }, 200);
    
    setTimeout(() => {
      clearInterval(interval);
      logo.style.color = ''; // Reset
    }, 3000);
  }

  createScrollWaveEffect() {
    const wave = document.createElement('div');
    wave.style.cssText = `
      position: fixed;
      top: 0;
      left: -100%;
      width: 100%;
      height: 4px;
      background: linear-gradient(90deg, transparent, #52c301, transparent);
      z-index: 9999;
      animation: wave-scroll 1s ease-out;
    `;
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes wave-scroll {
        0% { left: -100%; }
        100% { left: 100%; }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(wave);
    
    setTimeout(() => {
      wave.remove();
      style.remove();
    }, 1000);
  }

  createComboEffect() {
    // Create a subtle sparkle effect
    for (let i = 0; i < 10; i++) {
      setTimeout(() => {
        const sparkle = document.createElement('div');
        sparkle.textContent = '✨';
        sparkle.style.cssText = `
          position: fixed;
          top: ${Math.random() * 100}%;
          left: ${Math.random() * 100}%;
          font-size: 20px;
          pointer-events: none;
          z-index: 9999;
          animation: sparkle-fade 2s ease-out forwards;
        `;
        
        document.body.appendChild(sparkle);
        
        setTimeout(() => sparkle.remove(), 2000);
      }, i * 100);
    }
    
    // Add sparkle animation if not present
    if (!document.querySelector('#sparkle-animation')) {
      const style = document.createElement('style');
      style.id = 'sparkle-animation';
      style.textContent = `
        @keyframes sparkle-fade {
          0% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1) rotate(180deg); }
          100% { opacity: 0; transform: scale(0) rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }

  /**
   * Helper Methods
   */
  isLogo(element) {
    const logoSelectors = ['logo', 'header__heading', 'site-header__logo'];
    return logoSelectors.some(selector => 
      element.classList.contains(selector) || 
      element.closest(`.${selector}`)
    );
  }
}

// Initialize specific Easter eggs when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.madDadSpecificEggs = new MadDadSpecificEggs();
  });
} else {
  window.madDadSpecificEggs = new MadDadSpecificEggs();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MadDadSpecificEggs;
}